import { RawDraftContentBlock, RawDraftContentState } from 'draft-js'
import { TextBlockProps, TextPreset } from '../../types'

/**
 * "Convenience blocks": sidebar entries that add an ordinary Text block,
 * pre-filled with placeholder content in a common shape (from the product
 * prototype). They're presets, not block types: what's saved is plain rich
 * text, the same as migrated legacy content, so editors can reshape them
 * freely. The block remembers its preset only so the builder can offer
 * "+ Add date row" / "+ Add link" for the list presets.
 */
export type { TextPreset }

// Presets built from repeating rows, which the builder can add to
export type RowPreset = 'dateList' | 'linkList'

export const isRowPreset = (
  preset: TextPreset | undefined
): preset is RowPreset => preset === 'dateList' || preset === 'linkList'

const newKey = () => Math.random().toString(36).slice(2, 7)

type InlineStyle = RawDraftContentBlock['inlineStyleRanges'][number]['style']

const paragraph = (
  text: string,
  type = 'unstyled',
  styles: { offset: number; length: number; style: InlineStyle }[] = []
): RawDraftContentBlock => ({
  key: newKey(),
  text,
  type: type as RawDraftContentBlock['type'],
  depth: 0,
  inlineStyleRanges: styles,
  entityRanges: [],
  data: {}
})

/**
 * One placeholder row. Parts that are links in the prototype ("Details and
 * RSVP", the conversation title) are left unlinked: the editor links them
 * with the link button, rather than placeholder links getting published.
 */
export const createPresetRow = (preset: RowPreset): RawDraftContentBlock =>
  preset === 'dateList'
    ? // "6 - 7 May 2026 | World 50 Summit | New York City | Details and RSVP"
      paragraph('Date | Event name | City | Details and RSVP')
    : // "Conversation title with Guest Name | 13 February 2026"
      paragraph(
        'Conversation title with Guest Name | Date',
        'unordered-list-item'
      )

const PRESETS: { [P in TextPreset]: () => RawDraftContentBlock[] } = {
  dateList: () => [createPresetRow('dateList')],
  linkList: () => [createPresetRow('linkList')],
  // "**Overview:**" with the content on the next line, in one paragraph
  labeledSection: () => [
    paragraph('Label:\nSection content.', 'unstyled', [
      { offset: 0, length: 'Label:'.length, style: 'BOLD' }
    ])
  ],
  // "* Curated for our female members.", in italic small print
  footnote: () => [
    paragraph('* Footnote text.', 'small', [
      { offset: 0, length: '* Footnote text.'.length, style: 'ITALIC' }
    ])
  ]
}

export const createPresetRichText = (
  preset: TextPreset
): RawDraftContentState => ({ blocks: PRESETS[preset](), entityMap: {} })

export const createPresetProps = (preset: TextPreset): TextBlockProps => ({
  body: createPresetRichText(preset),
  preset
})

// The body with one more placeholder row at the end
export const appendPresetRow = (
  body: RawDraftContentState,
  preset: RowPreset
): RawDraftContentState => ({
  ...body,
  blocks: [...body.blocks, createPresetRow(preset)]
})
